import { boxIntersects, type Box } from '../model/geom';
import type { Micron } from '../model/units';

/**
 * 공간 인덱스 (작업지시서 P2-03)
 *
 * 선택 · 스냅 · 뷰포트 컬링이 전부 여기를 통과한다.
 * 5만 엔티티에서 매 프레임 전수 검사하면 팬/줌이 즉시 무너진다.
 *
 * 구현: **균등 격자 + 초대형 객체 별도 목록** 하이브리드.
 *
 *   왜 R-tree 가 아닌가 — 마트 평면도는 엔티티 크기가 비교적 균일하다
 *   (매대 0.9~2.4m, 벽체 세그먼트 수 m). 균등 격자가 삽입·삭제 O(1)에
 *   구현이 단순하고 의존성이 없다.
 *
 *   격자의 약점은 "도면 전체를 가로지르는 한 개의 선" 같은 초대형 객체다.
 *   이런 것이 격자를 가득 채우면 이점이 사라지므로, 일정 셀 수를 넘는
 *   객체는 격자에 넣지 않고 `oversized` 목록에 따로 둔 뒤 항상 검사한다.
 *
 *   엔티티 분포가 극단적으로 불균일해지면 rbush(R-tree)로 교체할 수 있도록
 *   인터페이스를 좁게 유지했다.
 */

/** 이 셀 수를 넘게 차지하는 객체는 격자에 넣지 않는다. */
const OVERSIZED_CELLS = 64;

export class SpatialGrid {
  private cellSize: Micron;
  private cells = new Map<number, Set<string>>();
  private boxes = new Map<string, Box>();
  private oversized = new Set<string>();

  /** @param cellSize 격자 한 칸 크기(µm). 기본 2m — 매대 2~3개 크기. */
  constructor(cellSize: Micron = 2_000_000) {
    this.cellSize = cellSize;
  }

  get size(): number {
    return this.boxes.size;
  }

  clear(): void {
    this.cells.clear();
    this.boxes.clear();
    this.oversized.clear();
  }

  /**
   * 셀 좌표를 하나의 정수 키로 접는다.
   * Map<number> 가 Map<string> 보다 눈에 띄게 빠르다.
   * ±32,768 셀(기본 셀 크기로 ±65km) 범위를 커버한다.
   */
  private key(cx: number, cy: number): number {
    return (cx + 32768) * 65536 + (cy + 32768);
  }

  private cellRange(b: Box): { x0: number; y0: number; x1: number; y1: number } {
    const s = this.cellSize;
    return {
      x0: Math.floor(b.minX / s),
      y0: Math.floor(b.minY / s),
      x1: Math.floor(b.maxX / s),
      y1: Math.floor(b.maxY / s),
    };
  }

  insert(id: string, box: Box): void {
    if (this.boxes.has(id)) this.remove(id);
    this.boxes.set(id, box);

    const r = this.cellRange(box);
    const cellCount = (r.x1 - r.x0 + 1) * (r.y1 - r.y0 + 1);

    if (cellCount > OVERSIZED_CELLS) {
      this.oversized.add(id);
      return;
    }

    for (let cx = r.x0; cx <= r.x1; cx++) {
      for (let cy = r.y0; cy <= r.y1; cy++) {
        const k = this.key(cx, cy);
        let set = this.cells.get(k);
        if (!set) {
          set = new Set();
          this.cells.set(k, set);
        }
        set.add(id);
      }
    }
  }

  remove(id: string): void {
    const box = this.boxes.get(id);
    if (!box) return;
    this.boxes.delete(id);

    if (this.oversized.delete(id)) return;

    const r = this.cellRange(box);
    for (let cx = r.x0; cx <= r.x1; cx++) {
      for (let cy = r.y0; cy <= r.y1; cy++) {
        const k = this.key(cx, cy);
        const set = this.cells.get(k);
        if (!set) continue;
        set.delete(id);
        if (set.size === 0) this.cells.delete(k);
      }
    }
  }

  update(id: string, box: Box): void {
    this.insert(id, box); // insert 가 기존 것을 먼저 제거한다
  }

  boxOf(id: string): Box | undefined {
    return this.boxes.get(id);
  }

  /**
   * 질의 상자와 겹칠 **가능성이 있는** id 들.
   * 격자는 경계 상자 단위이므로 정밀 판정은 호출자가 한 번 더 해야 한다.
   */
  query(box: Box): string[] {
    const found = new Set<string>();

    // 초대형 객체는 격자를 우회하므로 항상 검사한다.
    for (const id of this.oversized) {
      const b = this.boxes.get(id);
      if (b && boxIntersects(b, box)) found.add(id);
    }

    const r = this.cellRange(box);
    // 질의 범위가 지나치게 넓으면 격자를 도는 것보다 전수 검사가 빠르다.
    const cellCount = (r.x1 - r.x0 + 1) * (r.y1 - r.y0 + 1);
    if (cellCount > this.cells.size && this.cells.size > 0) {
      for (const [id, b] of this.boxes) {
        if (boxIntersects(b, box)) found.add(id);
      }
      return [...found];
    }

    for (let cx = r.x0; cx <= r.x1; cx++) {
      for (let cy = r.y0; cy <= r.y1; cy++) {
        const set = this.cells.get(this.key(cx, cy));
        if (!set) continue;
        for (const id of set) {
          if (found.has(id)) continue;
          const b = this.boxes.get(id);
          if (b && boxIntersects(b, box)) found.add(id);
        }
      }
    }
    return [...found];
  }

  /** 전체 엔티티를 감싸는 상자. ZOOM Extents 에 쓴다. */
  extent(): Box | null {
    let out: Box | null = null;
    for (const b of this.boxes.values()) {
      if (!out) {
        out = { ...b };
      } else {
        if (b.minX < out.minX) out.minX = b.minX;
        if (b.minY < out.minY) out.minY = b.minY;
        if (b.maxX > out.maxX) out.maxX = b.maxX;
        if (b.maxY > out.maxY) out.maxY = b.maxY;
      }
    }
    return out;
  }
}
